import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Avatar,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import Layout from '../components/Layout';
import companyService from '../services/companyService';
import addressService from '../services/addressService';
import StaffManagementTab from '../components/StaffManagementTab';
import { useCompany } from '../contexts/CompanyContext';

const CompanyFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getEffectiveCompanyId } = useCompany();
  const [user, setUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [companyExists, setCompanyExists] = useState(false);

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);

  const [formData, setFormData] = useState({
    id: '',
    name: '',
    taxId: '',
    slug: '',
    cityName: '',
    districtName: '',
    zipCode: '',
    detailAddress: '',
    address: '',
    phone: '',
    email: '',
    logoUrl: ''
  });

  const [activeTab, setActiveTab] = useState(0);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    // Load user from localStorage
    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
    } else {
      navigate('/login');
      return;
    }
    
    // Load cities
    const allCities = addressService.getAllCities();
    setCities(allCities);
    
  }, [navigate]);
  
  // Separate useEffect to fetch company data after user is set
  useEffect(() => {
    if (user) {
      fetchCompanyData();
    }
  }, [user]);


  const fetchCompanyData = async () => {
    try {
      setLoading(true);

      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      const isProfilePath = location.pathname === '/company/profile';
      
      let companyData;
      
      if (isAdmin && id) {
        // Admin editing a specific company
        try {
          companyData = await companyService.getCompanyById(id);
        } catch (apiError) {
          throw apiError;
        }
        setEditMode(true); // Admin always starts in edit mode
      } else if (isProfilePath || !isAdmin) {
        // Regular user viewing their own company

        const effectiveCompanyId = getEffectiveCompanyId();
        if (effectiveCompanyId) {
          try {
            companyData = await companyService.getCompanyById(effectiveCompanyId);
          } catch (apiError) {
            throw apiError;
          }
          setEditMode(false); // Regular user starts in view mode
        } else {
          // No company associated with user
          setEditMode(true);
          setCompanyExists(false);
          setLoading(false);
          return;
        }
      } else {
        // Admin creating a new company
        setEditMode(true);
        setCompanyExists(false);
        setLoading(false);
        return;
      }
      
      if (companyData) {
        setCompanyExists(true);

        // Parse address into components
        const addressComponents = addressService.parseAddress(companyData.address);

        // Set form data with combined address components
        setFormData({
          ...companyData,
          ...addressComponents
        });

        // Load districts for the selected city
        if (addressComponents.cityName) {
          const districtsList = addressService.getDistrictsByCity(addressComponents.cityName);
          setDistricts(districtsList);
        }
      } else {
        // No existing company data, start in edit mode
        setEditMode(true);
        setCompanyExists(false);
      }
    } catch (error) {
      setNotification({
        open: true,
        message: '無法找到該公司資料',
        severity: 'error'
      });
      
      // If there's an error, still end loading state
      // If no company data found for regular user, navigate back to home or create new
      if (location.pathname === '/company/profile') {
        setEditMode(true);
        setCompanyExists(false);
      } else if (id) {
        // If admin was trying to edit a specific company by ID that doesn't exist
        setTimeout(() => navigate('/company'), 2000);
      }
    } finally {
      setLoading(false);
    }
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for city selection
    if (name === 'cityName') {
      // When city changes, reset district and update districts list
      setFormData(prev => ({
        ...prev,
        cityName: value,
        districtName: '',
        zipCode: ''
      }));
      
      // Update districts based on selected city
      setDistricts(addressService.getDistrictsByCity(value));
    } 
    // Special handling for district selection
    else if (name === 'districtName') {
      const zipCode = addressService.getZipCode(formData.cityName, value);
      setFormData(prev => ({
        ...prev,
        districtName: value,
        zipCode: zipCode || ''
      }));
    } 
    // Default handling for other fields
    else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Update the full address whenever address components change
    if (['cityName', 'districtName', 'detailAddress'].includes(name)) {
      setTimeout(() => {
        setFormData(prev => {
          const newValue = name === 'cityName' ? value : (name === 'districtName' ? value : prev[name]);
          const updatedAddress = addressService.formatAddress(
            name === 'cityName' ? value : prev.cityName,
            name === 'districtName' ? value : prev.districtName,
            name === 'detailAddress' ? value : prev.detailAddress
          );
          
          return {
            ...prev,
            [name]: newValue,
            address: updatedAddress
          };
        });
      }, 0);
    }
  };

  const handleLogoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          logoUrl: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // Update full address once more to ensure it's correct
      const fullAddress = addressService.formatAddress(
        formData.cityName,
        formData.districtName,
        formData.detailAddress
      );
      
      // Create FormData object for file upload
      const formDataToSubmit = new FormData();
      
      // Create a filtered data object that excludes address components
      // that shouldn't be sent to the backend
      const dataToSubmit = {
        ...formData,
        address: fullAddress // Ensure the address is properly formatted
      };
      
      // Remove fields that shouldn't be sent to the backend
      delete dataToSubmit.cityName;
      delete dataToSubmit.districtName;
      delete dataToSubmit.zipCode;
      delete dataToSubmit.detailAddress;
      delete dataToSubmit.logoUrl;
      
      // Skip id for new companies, as it's auto-incremented
      if (!companyExists) {
        delete dataToSubmit.id;
      }
      
      // Append all form fields.
      // 跳過 null / undefined — FormData.append 會把它們轉成字串 "null" / "undefined"，
      // 而表單會把後端載入的整列公司資料 (含未顯示的 customerServiceEmail / ccEmail 等)
      // 一併回送；若其值為 null 就會變成 "null"，觸發後端 email 格式驗證 → 「客服郵件格式不正確」。
      // 跳過後,後端會以「未提供 = 保留原值」處理 (customerServiceEmail !== undefined ? ...)。
      Object.keys(dataToSubmit).forEach(key => {
        const value = dataToSubmit[key];
        if (value !== null && value !== undefined) {
          formDataToSubmit.append(key, value);
        }
      });
      
      // Add logo file if it exists
      if (logoFile) {
        formDataToSubmit.append('logo', logoFile);
      }
      
      // Call the appropriate API method
      let result;
      if (companyExists) {
        result = await companyService.updateCompany(formData.id, formDataToSubmit);
      } else {
        result = await companyService.createCompany(formDataToSubmit);
        
        // Update user object in localStorage with the new company ID
        if (result && result.id) {
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser) {
            storedUser.companyId = result.id;
            localStorage.setItem('user', JSON.stringify(storedUser));
            
            // Also update the current user state
            setUser(storedUser);
          }
        }
        // *** END OF NEW CODE ***
      }
      
      // Show success notification
      setNotification({
        open: true,
        message: `公司資料${companyExists ? '更新' : '新增'}成功`,
        severity: 'success'
      });

      // 即時通知側邊欄更新品牌 (店名/logo) — 不用刷新整頁。Layout 監聽此事件。
      window.dispatchEvent(new CustomEvent('company:brandUpdated', {
        detail: {
          name: result?.name || formData.name,
          logoUrl: result?.logoUrl || ''
        }
      }));

      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      const isProfilePath = location.pathname === '/company/profile';
      
      // For admin, redirect to company list
      if (isAdmin && !isProfilePath) {
        setTimeout(() => {
          navigate('/company');
        }, 1500);
      } else {
        // For regular user or admin in profile, refresh data and exit edit mode
        setEditMode(false);
        fetchCompanyData();
      }
    } catch (error) {
      setNotification({
        open: true,
        message: `公司資料${companyExists ? '更新' : '新增'}失敗: ${error.response?.data?.message || '請稍後再試'}`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    const isAdmin = user?.role === 'admin';
    const isProfilePath = location.pathname === '/company/profile';
    
    if (isAdmin && !isProfilePath) {
      // For admin, go back to company list
      navigate('/company');
    } else if (companyExists) {
      // For regular user with existing company, exit edit mode
      setEditMode(false);
      fetchCompanyData(); // Reset form data
    } else {
      // For regular user creating a new company, reset form
      setFormData({
        id: '',
        name: '',
        taxId: '',
        cityName: '',
        districtName: '',
        zipCode: '',
        detailAddress: '',
        address: '',
        phone: '',
        email: '',
        logoUrl: ''
      });
    }
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Determine if fields should be disabled
  const isFieldDisabled = !editMode || submitting;

  // Get user role（admin 或 super_admin 皆可）
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isProfilePath = location.pathname === '/company/profile';

  return (
    <Layout>
      <Typography variant="h5" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
        {isAdmin && !isProfilePath
          ? (companyExists ? '編輯公司資料' : '新增公司資料')
          : '公司設定'}
      </Typography>

      {/* Tabs — 只有 admin 在 profile 頁面才顯示 */}
      {isAdmin && isProfilePath && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="公司資料" />
            <Tab label="員工管理" />
          </Tabs>
        </Paper>
      )}

      {/* Tab 1: 員工管理 */}
      {isAdmin && isProfilePath && activeTab === 1 && (
        <StaffManagementTab />
      )}

      {/* Tab 0: 公司資料 (原有表單) */}
      {(activeTab === 0 || !isAdmin || !isProfilePath) && (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 2, 
          borderRadius: '8px',
          border: '1px solid rgba(0,0,0,0.1)',
          background: '#fff'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 500, color: '#333' }}>
            基本資料
          </Typography>

          {/* Logo and Upload */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <Avatar 
              src={formData.logoUrl} 
              alt={formData.name || '公司標誌'}
              sx={{ 
                width: 80, 
                height: 80, 
                mb: 1,
                bgcolor: '#e0e0e0'
              }}
            />
            {editMode && (
              <>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleLogoChange}
                />
                <label htmlFor="logo-upload">
                  <IconButton 
                    color="primary" 
                    aria-label="upload picture" 
                    component="span"
                    disabled={submitting}
                    sx={{ 
                      mt: -6, 
                      ml: 7, 
                      bgcolor: 'white', 
                      border: '1px solid #eee',
                      '&:hover': { 
                        bgcolor: '#f5f5f5' 
                      } 
                    }}
                  >
                    <PhotoCamera />
                  </IconButton>
                </label>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  上傳公司標誌
                </Typography>
              </>
            )}
          </Box>

          <Grid container spacing={1}>
            {/* Basic Info Section */}
           
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="公司名稱"
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="輸入公司名稱"
                disabled={isFieldDisabled}
                variant="outlined"       
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="統一編號"
                name="taxId"
                fullWidth
                value={formData.taxId || ''}
                onChange={handleChange}
                placeholder="輸入統一編號"
                disabled={isFieldDisabled}
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>

            {/* Slug — voice asset folder name. Required for voice customers,
                immutable after first save unless super_admin. */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug (語音資產資料夾)"
                name="slug"
                fullWidth
                value={formData.slug || ''}
                onChange={handleChange}
                placeholder="例: ramen_taro"
                disabled={
                  isFieldDisabled ||
                  (!!companyExists && !!formData.slug && user?.role !== 'super_admin')
                }
                helperText={
                  formData.slug && user?.role !== 'super_admin'
                    ? '如須修改請聯繫 support@rebaz.ai'
                    : '小寫字母開頭、3-32 字元、僅含 a-z 0-9 _。語音客戶必填'
                }
                inputProps={{ pattern: '^[a-z][a-z0-9_]{2,31}$', maxLength: 32 }}
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>

            {/* Address Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 500, color: '#333' }}>
                公司地址
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} size="small">
                <InputLabel id="city-label">縣/市</InputLabel>
                <Select
                  labelId="city-label"
                  name="cityName"
                  value={formData.cityName || ''}
                  onChange={handleChange}
                  label="縣/市"
                  disabled={isFieldDisabled}
                  sx={{
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: '#212121', // ✅ for Chrome/Safari
                      color: '#212121',               // ✅ fallback
                    }
                  }}
                >
                  {cities.map(city => (
                    <MenuItem key={city.name} value={city.name}>{city.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} size="small">
                <InputLabel id="district-label">區</InputLabel>
                <Select
                  labelId="district-label"
                  name="districtName"
                  value={formData.districtName || ''}
                  onChange={handleChange}
                  label="區"
                  disabled={!formData.cityName || isFieldDisabled}
                  sx={{
                    '& .MuiSelect-select.Mui-disabled': {
                      WebkitTextFillColor: '#212121', // ✅ for Chrome/Safari
                      color: '#212121',               // ✅ fallback
                    }
                  }}
                >
                  {districts.map(district => (
                    <MenuItem key={district.name} value={district.name}>
                      {district.name} ({district.zipCode})
                    </MenuItem>
                  ))}
                  </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="詳細地址"
                name="detailAddress"
                fullWidth
                value={formData.detailAddress || ''}
                onChange={handleChange}
                placeholder="街道、門牌號碼等"
                disabled={isFieldDisabled}
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>

            {/* Contact Information Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1, fontWeight: 500, color: '#333' }}>
                聯絡資訊
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="電話"
                name="phone"
                fullWidth
                value={formData.phone || ''}
                onChange={handleChange}
                placeholder="輸入電話號碼"
                disabled={isFieldDisabled}
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="電子郵件"
                name="email"
                type="email"
                fullWidth
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="輸入電子郵件"
                disabled={isFieldDisabled}
                variant="outlined"
                sx={{
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "#212121",
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ 
            mt: 2, 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: 2 
          }}>
            {editMode ? (
              // Edit mode buttons (Cancel and Submit)
              <>
                <Button 
                  variant="outlined" 
                  onClick={handleCancel}
                  disabled={submitting}
                  sx={{ 
                    px: 3, 
                    py: 0.5,
                    color: '#666',
                    borderColor: '#ccc',
                    '&:hover': {
                      borderColor: '#999',
                      backgroundColor: 'rgba(0,0,0,0.03)'
                    }
                  }}
                >
                  取消
                </Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={submitting}
                  sx={{ 
                    px: 3,
                    py: 0.5,
                    bgcolor: 'primary', 
                  }}
                >
                  {submitting ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                      處理中...
                    </>
                  ) : (
                    '送出'
                  )}
                </Button>
              </>
            ) : (
              // View mode button (Edit)
              <Button 
                variant="contained" 
                onClick={handleEdit}
                startIcon={<EditIcon />}
                sx={{ 
                  px: 3,
                  py: 0.5,
                  bgcolor: 'primary', 
                }}
              >
                修改
              </Button>
            )}
          </Box>
        </form>
      </Paper>
      )}

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default CompanyFormPage;